/*
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2022 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers. The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { FacetModel } from "@insite/client-framework/Types/ApiModels";
import { AccordionSectionPresentationProps, ManagedAccordionSection } from "@insite/mobius/AccordionSection";
import Checkbox, { CheckboxPresentationProps } from "@insite/mobius/Checkbox/Checkbox";
import CheckboxGroup, { CheckboxGroupProps } from "@insite/mobius/CheckboxGroup/CheckboxGroup";
import Link, { LinkProps } from "@insite/mobius/Link/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { color, styledScrollbar } from "@stoneage/XDL/Utilities";
import { blackCheckbox } from "@stoneage/XDL/Utilities/SharedStyles/blackCheckbox";
import React, { FC, useState } from "react";
import { css } from "styled-components";

export interface ProductListFilterAccordionSectionStyles {
    sectionWrapper?: InjectableCss;
    accordionSection?: AccordionSectionPresentationProps;
    checkBoxGroup?: CheckboxGroupProps;
    checkBox?: CheckboxPresentationProps;
    checkBoxSelected?: CheckboxPresentationProps;
    loadMoreLink?: LinkProps;
}

export const productListFilterAccordionSectionStyles: ProductListFilterAccordionSectionStyles = {
    sectionWrapper: {
        css: css`
            margin-bottom: 4px;
        `,
    },
    accordionSection: {
        panelProps: {
            css: css`
                background: ${color("common.ui4")};
                padding-top: 4px;
                overflow-y: auto;
                overflow-x: hidden;
                max-height: 350px;
                ${styledScrollbar(8, "common.ui7")};
            `,
        },
    },
    checkBoxGroup: {
        css: css`
            width: 100%;
            border-radius: 0px;
            legend {
                display: none;
            }
        `,
    },
    ...blackCheckbox,
    loadMoreLink: {
        color: "secondary",
        typographyProps: {
            size: 12,
            weight: "bold",
            css: css`
                &&& :hover {
                    color: ${color("primary")};
                }
            `,
        },
        css: css`
            padding-top: 4px;
        `,
    },
    checkBox: {
        css: css`
            span[role="checkbox"] {
                cursor: pointer;
            }
        `,
        mergeCss: true,
    },
};

export interface Props {
    title: string;
    facets: FacetModel[];
    onChangeFacet: (facet: FacetModel) => void;
    showMoreLimit: number;
    extendedStyles?: ProductListFilterAccordionSectionStyles;
    expandByDefault: boolean;
}

const ProductListFiltersAccordionSection: FC<Props> = ({
    title,
    facets,
    onChangeFacet,
    showMoreLimit,
    expandByDefault,
    extendedStyles,
}) => {
    const [styles] = React.useState(() => mergeToNew(productListFilterAccordionSectionStyles, extendedStyles));
    const [expanded, setExpanded] = useState(false);

    const limitedFacets = expanded ? facets : facets?.slice(0, showMoreLimit);
    const anySelected = facets.find(f => f.selected) !== undefined;

    return (
        <StyledWrapper {...styles.sectionWrapper}>
            <ManagedAccordionSection
                title={title}
                {...styles.accordionSection}
                initialExpanded={expandByDefault || anySelected}
            >
                {limitedFacets?.map(facet => (
                    <CheckboxGroup key={facet.id} {...styles.checkBoxGroup}>
                        <Checkbox
                            {...(facet.selected ? styles.checkBoxSelected : styles.checkBox)}
                            checked={facet.selected}
                            onChange={() => onChangeFacet(facet)}
                            data-test-selector={`facetFilter${facet.id}`}
                        >
                            {facet.name} {facet.count !== -1 && `(${facet.count})`}
                        </Checkbox>
                    </CheckboxGroup>
                ))}
                {facets &&
                    facets.length > showMoreLimit &&
                    (expanded ? (
                        <Link {...styles.loadMoreLink} onClick={() => setExpanded(false)}>
                            {translate("Load Less -")}
                        </Link>
                    ) : (
                        <Link {...styles.loadMoreLink} onClick={() => setExpanded(true)}>
                            {translate("Load More +")}
                        </Link>
                    ))}
            </ManagedAccordionSection>
        </StyledWrapper>
    );
};

export default ProductListFiltersAccordionSection;
